export const TRANSLATION_PREFIX = 'STATIC.PAGES.REFERENCE_TABLE';

// Initial number of rows rendered, and how many more are revealed per infinite-scroll tick.
export const INITIAL_ROW_LIMIT = 250;
export const ROW_LIMIT_INCREMENT = 10;

// Rows are edited in the browser; larger tables must be imported/exported instead of hand-saved.
export const MAX_ROWS_FOR_MANUAL_CHANGES = 10000;

export const MAX_PHYSICAL_ROWS_TO_SAVE = 30000;

export const SUCCESS_TOAST_AUTO_CLOSE = 5000;

export const VALUE_CURRENCY_FIELD = 'value_currency';
export const EVENT_FIELD = 'event';
export const COUNTRY_FIELD = 'country';
export const COLLAPSED_FIELDS = [COUNTRY_FIELD];

// The campaign list is filtered server side, so a keystroke is a request.
export const SEARCH_DEBOUNCE_MS = 300;

// How many campaigns to ask for. The endpoint has no paging the grid uses, so this is the whole
// first page and anything beyond it is only reachable by typing. 500 is the endpoint's own cap.
export const CAMPAIGNS_LIMIT = 500;

// The dictionary-backed columns, in the display order used by the legacy Angular page.
// `field` is the row/payload key, `dict` is the dictionary that validates the cell.
export const COLUMNS = [
    {
        field: 'app',
        label: `${TRANSLATION_PREFIX}.COLUMNS.APP`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.APP`,
        dict: 'app',
    },
    {
        field: 'source',
        label: `${TRANSLATION_PREFIX}.COLUMNS.SOURCE`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.SOURCE`,
        dict: 'source',
    },
    {
        field: 'sub_adnetwork',
        label: `${TRANSLATION_PREFIX}.COLUMNS.SUB_ADNETWORK`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.SUB_ADNETWORK`,
        dict: 'sub_adnetwork',
    },
    {
        field: 'campaign_name',
        label: `${TRANSLATION_PREFIX}.COLUMNS.CAMPAIGN_NAME`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.CAMPAIGN_NAME`,
        dict: 'campaign_name',
        // Both per-row dictionaries depend on cells to their left, which is not visible from the
        // cell itself - the header says so rather than leaving an empty dropdown unexplained.
        tooltip: `${TRANSLATION_PREFIX}.COLUMN_TOOLTIP.CAMPAIGN_NAME`,
    },
    {
        field: 'event',
        label: `${TRANSLATION_PREFIX}.COLUMNS.EVENT`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.EVENT`,
        dict: 'event',
        tooltip: `${TRANSLATION_PREFIX}.COLUMN_TOOLTIP.EVENT`,
    },
    {
        field: 'os',
        label: `${TRANSLATION_PREFIX}.COLUMNS.OS`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.OS`,
        dict: 'os',
    },
    {
        field: 'device',
        label: `${TRANSLATION_PREFIX}.COLUMNS.DEVICE`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.DEVICE`,
        dict: 'device',
    },
    {
        field: 'country',
        label: `${TRANSLATION_PREFIX}.COLUMNS.COUNTRY`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.COUNTRY`,
        dict: 'country',
    },
    {
        field: 'adn_account_id',
        label: `${TRANSLATION_PREFIX}.COLUMNS.ACCOUNT_ID`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.ACCOUNT_ID`,
        dict: 'adn_account_id',
    },
    {
        field: 'adn_original_currency',
        label: `${TRANSLATION_PREFIX}.COLUMNS.COST_CURRENCY`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.CURRENCY`,
        dict: 'adn_original_currency',
    },
    {
        field: 'tracker_original_currency',
        label: `${TRANSLATION_PREFIX}.COLUMNS.REVENUE_CURRENCY`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.CURRENCY`,
        dict: 'tracker_original_currency',
    },
];

// Every dictionary-backed field, including the optional value-currency column.
export const DICT_FIELDS = [...COLUMNS.map(column => column.field), VALUE_CURRENCY_FIELD];

// Columns whose dictionary is fetched per row, not per table (a table can hold several apps).
// `parents` is both the cache key and the set of cells that clear this one when they change.
// `requiresScope` means the endpoint can't be called without them: events need an app_id.
export const PER_ROW_DICTS = {
    event: { parents: ['app'], requiresScope: true },
    campaign_name: { parents: ['app', 'source'], requiresScope: false },
};

// The CSV column key for the date column (kept identical to the legacy export/import contract).
export const CSV_DATE_HEADER = 'Rule Start Date';
export const CSV_VALUE_HEADER = 'value';

// Example cell values shown in the import modal's "Expected format" table, keyed by CSV column.
// Illustrative only — the actual accepted values come from the on-page dropdowns.
export const CSV_COLUMN_EXAMPLES = {
    [CSV_DATE_HEADER]: '2026-07-14',
    app: 'com.company.app',
    source: 'facebook',
    sub_adnetwork: 'facebook',
    campaign_name: 'Summer_US_iOS',
    // The dropdown label, not the event uuid.
    event: 'Purchase',
    os: 'iOS',
    device: 'phone',
    country: 'US',
    adn_account_id: '1234567890',
    adn_original_currency: 'USD',
    tracker_original_currency: 'USD',
    [CSV_VALUE_HEADER]: '1250.00',
    [VALUE_CURRENCY_FIELD]: 'USD',
};

// Per-table help content, keyed by the table title returned from the backend.
export const TABLE_FAQ_LINKS = {
    'CPI Configuration':
        'https://support.singular.net/hc/en-us/articles/53658179746587-Reference-Tables-FAQ-Partner-Fees-and-CPI-Configuration',
    'Partner Fees':
        'https://support.singular.net/hc/en-us/articles/53658179746587-Reference-Tables-FAQ-Partner-Fees-and-CPI-Configuration',
};

// Value-column tooltip, keyed by table slug (matches the legacy ng-template tooltip).
export const VALUE_TOOLTIP_BY_SLUG = {
    'partner-fees': `${TRANSLATION_PREFIX}.VALUE_TOOLTIP.PARTNER_FEES`,
    'cpi-configuration': `${TRANSLATION_PREFIX}.VALUE_TOOLTIP.CPI_CONFIGURATION`,
};
